import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Grid, Tabs, Tab, CircularProgress, Alert,
  TextField, MenuItem, Chip, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';

import {
  getSegmentationRosterReport, getSegmentationPerformanceReport, getSegmentationOverlapReport,
  getSegmentationEvaluationsReport, getSegmentationSourceMixReport,
  getSegmentationLifecycleReport, getSegmentationCoverageReport,
} from '../../services/api';
import KpiCard from '../../components/reports/KpiCard';
import DistributionBar from '../../components/reports/DistributionBar';
import TableWithBars from '../../components/reports/TableWithBars';
import Heatmap from '../../components/reports/Heatmap';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import ReportExportButton from '../../components/reports/ReportExportButton';

const fmtPct = (v, d = 1) => v == null ? '—' : `${(v * 100).toFixed(d)}%`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString();

function ReportSection({ title, action, children }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function useReport(loader, ...args) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const argsKey = JSON.stringify(args);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await loader(...args);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.title || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [loader, argsKey]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error };
}

// S1
function RosterCard() {
  const { data, loading, error } = useReport(getSegmentationRosterReport, {});
  const rows = data?.data?.segments || [];
  return (
    <ReportSection
      title="S1 · Segment Roster"
      action={<ReportExportButton path="/reports/segmentations/roster/export.csv" filters={{}} filename="segmentation-roster.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="members"
          columns={[
            { key: 'name', label: 'Segment' },
            { key: 'type', label: 'Type', render: (r) => <Chip size="small" label={r.type} variant="outlined" /> },
            { key: 'members', label: 'Members', align: 'right' },
            { key: 'growth7d', label: '+7d', align: 'right', render: (r) => `+${r.growth7d}` },
            { key: 'growth30d', label: '+30d', align: 'right', render: (r) => `+${r.growth30d}` },
            { key: 'churn30d', label: '−30d', align: 'right', render: (r) => `-${r.churn30d}` },
            { key: 'autoRefresh', label: 'Auto', align: 'center', render: (r) => r.autoRefresh ? 'Yes' : 'No' },
            { key: 'warnings', label: 'Warn', align: 'right', render: (r) => r.warnings > 0 ? <Chip size="small" label={r.warnings} color="warning" /> : '0' },
          ]}
          rows={rows.map((r) => ({ id: r.id, ...r }))}
        />
      )}
    </ReportSection>
  );
}

// S2
function PerformanceCard({ filters }) {
  const { data, loading, error } = useReport(getSegmentationPerformanceReport, filters);
  const baseline = data?.data?.baseline || { convRate: 0, replyRate: 0 };
  const segs = data?.data?.segments || [];
  return (
    <ReportSection
      title="S2 · Segment Performance vs Baseline"
      action={<ReportExportButton path="/reports/segmentations/performance/export.csv" filters={filters} filename="segmentation-performance.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <KpiCard label="Baseline Conv %" value={fmtPct(baseline.convRate)} tone="info" />
            <KpiCard label="Baseline Reply %" value={fmtPct(baseline.replyRate)} tone="info" />
          </Stack>
          <TableWithBars
            barColumn="members"
            columns={[
              { key: 'name', label: 'Segment' },
              { key: 'members', label: 'Members', align: 'right' },
              { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => fmtPct(r.convRate) },
              { key: 'liftConv', label: 'vs base', align: 'right', render: (r) => `${(r.convRate - baseline.convRate >= 0 ? '+' : '')}${((r.convRate - baseline.convRate) * 100).toFixed(1)}pp` },
              { key: 'replyRate', label: 'Reply %', align: 'right', render: (r) => fmtPct(r.replyRate) },
              { key: 'avgFit', label: 'Avg fit', align: 'right', render: (r) => r.avgFit ?? '—' },
            ]}
            rows={segs.map((r) => ({ id: r.id, ...r }))}
          />
        </>
      )}
    </ReportSection>
  );
}

// S3
function OverlapCard() {
  const { data, loading, error } = useReport(getSegmentationOverlapReport);
  const segments = data?.data?.segments || [];
  const matrix = data?.data?.matrix || [];
  return (
    <ReportSection title="S3 · Segment Overlap (Jaccard)">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : segments.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No segments.</Typography>
      ) : (
        <Heatmap
          rows={segments}
          columns={segments.map((s) => s.name)}
          rowLabel={(s) => s.name}
          getValue={(rowSeg, colName) => {
            const i = segments.findIndex((s) => s.id === rowSeg.id);
            const j = segments.findIndex((s) => s.name === colName);
            if (i < 0 || j < 0) return 0;
            return Math.round((matrix?.[i]?.[j] || 0) * 100);
          }}
          baseColor={[245, 158, 11]}
        />
      )}
    </ReportSection>
  );
}

// S4
function EvaluationsCard({ segments, filters }) {
  const [segmentId, setSegmentId] = useState('');
  useEffect(() => {
    if (!segmentId && segments.length) setSegmentId(segments[0].id);
  }, [segments, segmentId]);
  const f = useMemo(() => filters, [filters]);
  const enabled = !!segmentId;
  const { data, loading, error } = useReport(
    enabled ? getSegmentationEvaluationsReport : (() => Promise.resolve({ data: { data: { evaluations: [] } } })),
    segmentId,
    f,
  );
  const rows = data?.data?.evaluations || [];
  return (
    <ReportSection
      title="S4 · Rule Effectiveness"
      action={
        <TextField size="small" select label="Segment" value={segmentId} onChange={(e) => setSegmentId(e.target.value)} sx={{ minWidth: 220 }}>
          {segments.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
      }
    >
      {!enabled ? <Typography variant="caption" color="text.secondary">Pick a segment.</Typography>
        : loading ? <CircularProgress size={24} />
        : error ? <Alert severity="error">{error}</Alert>
        : rows.length === 0 ? <Typography variant="caption" color="text.secondary">No evaluation runs in window. Wait for next AutoRefresh cycle or run manually.</Typography>
        : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>At</TableCell>
                <TableCell align="right">Matched</TableCell>
                <TableCell align="right">Added</TableCell>
                <TableCell align="right">Removed</TableCell>
                <TableCell align="right">Kept</TableCell>
                <TableCell align="right">Duration (ms)</TableCell>
                <TableCell>Warnings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e, i) => (
                <TableRow key={i}>
                  <TableCell>{new Date(e.at).toLocaleString()}</TableCell>
                  <TableCell align="right">{e.matched}</TableCell>
                  <TableCell align="right" sx={{ color: '#16a34a' }}>+{e.added}</TableCell>
                  <TableCell align="right" sx={{ color: '#dc2626' }}>-{e.removed}</TableCell>
                  <TableCell align="right">{e.kept}</TableCell>
                  <TableCell align="right">{e.durationMs}</TableCell>
                  <TableCell>{e.warnings.length === 0 ? '—' : e.warnings.map((w, k) => <Chip key={k} size="small" label={w} color="warning" sx={{ mr: 0.5 }} />)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
    </ReportSection>
  );
}

// S5
function SourceMixCard({ segments }) {
  const [segmentId, setSegmentId] = useState('');
  useEffect(() => {
    if (!segmentId && segments.length) setSegmentId(segments[0].id);
  }, [segments, segmentId]);
  const enabled = !!segmentId;
  const { data, loading, error } = useReport(
    enabled ? getSegmentationSourceMixReport : (() => Promise.resolve({ data: { data: { mix: {}, avgConfidence: {} } } })),
    segmentId,
  );
  const mix = data?.data?.mix || {};
  const conf = data?.data?.avgConfidence || {};
  const overrides = data?.data?.manualOverrides || 0;
  return (
    <ReportSection
      title="S5 · AssignSource Mix"
      action={
        <TextField size="small" select label="Segment" value={segmentId} onChange={(e) => setSegmentId(e.target.value)} sx={{ minWidth: 220 }}>
          {segments.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
      }
    >
      {!enabled ? <Typography variant="caption" color="text.secondary">Pick a segment.</Typography>
        : loading ? <CircularProgress size={24} />
        : error ? <Alert severity="error">{error}</Alert>
        : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Source distribution</Typography>
              <DistributionBar
                rows={Object.entries(mix).map(([k, v]) => ({
                  label: k,
                  value: v,
                  note: conf[k] != null ? `avg conf ${(conf[k] * 100).toFixed(0)}%` : null,
                }))}
                valueFormatter={fmtInt}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <KpiCard label="Manual overrides" value={fmtInt(overrides)} tone={overrides > 0 ? 'warning' : 'neutral'} />
            </Grid>
          </Grid>
        )}
    </ReportSection>
  );
}

// S6
function LifecycleCard({ filters }) {
  const { data, loading, error } = useReport(getSegmentationLifecycleReport, filters);
  const d = data?.data || {};
  return (
    <ReportSection title="S6 · Segment Lifecycle">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}><KpiCard label="New (window)" value={fmtInt(d.newCount)} tone="info" /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Auto-created" value={fmtInt(d.autoCreated)} tone="info" /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Human-created" value={fmtInt(d.humanCreated)} tone="info" /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Archived" value={fmtInt(d.archivedCount)} tone="neutral" /></Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Size trend (last 30d)</Typography>
            <Stack direction="row" spacing={2}>
              <Chip label={`Growing ${d.size?.growing || 0}`} color="success" />
              <Chip label={`Stable ${d.size?.stable || 0}`} />
              <Chip label={`Shrinking ${d.size?.shrinking || 0}`} color="warning" />
            </Stack>
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

// S7
function CoverageCard() {
  const { data, loading, error } = useReport(getSegmentationCoverageReport);
  const d = data?.data || {};
  return (
    <ReportSection title="S7 · Lead-to-Segment Coverage">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}><KpiCard label="Total leads" value={fmtInt(d.totalLeads)} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Segmented" value={fmtInt(d.inAnySegment)} sub={fmtPct(d.coverageRate)} tone="positive" /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Unsegmented" value={fmtInt(d.inNoSegment)} tone={d.inNoSegment > 0 ? 'warning' : 'neutral'} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Over-tagged (>3)" value={fmtInt(d.inOver3Segments)} tone={d.inOver3Segments > 0 ? 'warning' : 'neutral'} /></Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'roster', label: 'Roster (S1)' },
  { key: 'performance', label: 'Performance (S2)' },
  { key: 'overlap', label: 'Overlap (S3)' },
  { key: 'evaluations', label: 'Evaluations (S4)' },
  { key: 'source-mix', label: 'Source Mix (S5)' },
  { key: 'lifecycle', label: 'Lifecycle (S6)' },
  { key: 'coverage', label: 'Coverage (S7)' },
];

export default function SegmentationReportPage() {
  const [filters, setFilters] = useState({ window: '30d' });
  const [tab, setTab] = useState('overview');
  const { data: rosterData } = useReport(getSegmentationRosterReport, {});
  const segments = useMemo(() => (rosterData?.data?.segments || []).map((s) => ({ id: s.id, name: s.name })), [rosterData]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <RuleOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Segmentation Reports</Typography>
        <Box flex={1} />
        <ReportFilterBar value={filters} onChange={setFilters} />
      </Stack>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>
      {tab === 'overview' && (
        <>
          <CoverageCard />
          <RosterCard />
          <PerformanceCard filters={filters} />
        </>
      )}
      {tab === 'roster' && <RosterCard />}
      {tab === 'performance' && <PerformanceCard filters={filters} />}
      {tab === 'overlap' && <OverlapCard />}
      {tab === 'evaluations' && <EvaluationsCard segments={segments} filters={filters} />}
      {tab === 'source-mix' && <SourceMixCard segments={segments} />}
      {tab === 'lifecycle' && <LifecycleCard filters={filters} />}
      {tab === 'coverage' && <CoverageCard />}
    </Box>
  );
}
